import { createSlice } from '@reduxjs/toolkit';

const storedToken = localStorage.getItem('token');
const storedRefreshToken = localStorage.getItem('refresh_token');
const storedVerificationEmail = localStorage.getItem('pendingVerificationEmail');
const initialState = {
  user: null,
  token: storedToken,
  refreshToken: storedRefreshToken,
  isAuthenticated: false, // Only true when we have both token and user data
  loading: false,
  error: null,
  registrationStep: storedVerificationEmail ? 'verification' : 'form', // 'form' | 'verification' | 'success'
  verificationEmail: storedVerificationEmail,
  initializing: !!storedToken, // Start as true if we have a token to check, false otherwise
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      // Only set authenticated if we have both token and user
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = !!(action.payload.user && action.payload.token);
      localStorage.setItem('token', action.payload.token);
      if (action.payload.refreshToken) {
        localStorage.setItem('refresh_token', action.payload.refreshToken);
      }
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    registerStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    registerSuccess: (state, action) => {
      state.loading = false;
      state.registrationStep = 'verification';
      state.verificationEmail = action.payload.email;
      // Persist verification email in localStorage so user can return to verification screen
      localStorage.setItem('pendingVerificationEmail', action.payload.email);
    },
    registerFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.registrationStep = 'form';
    },
    setRegistrationStep: (state, action) => {
      state.registrationStep = action.payload;
    },
    verifyEmailStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    verifyEmailSuccess: (state, action) => {
      state.loading = false;
      state.registrationStep = 'success';
      state.user = action.payload.user;
      // Clear pending verification email from localStorage after successful verification
      localStorage.removeItem('pendingVerificationEmail');
      state.verificationEmail = null;
    },
    verifyEmailFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setVerificationEmail: (state, action) => {
      state.verificationEmail = action.payload;
    },
    resetRegistration: (state) => {
      // Don't clear verification email from localStorage here - keep it so user can return
      // Only reset the UI state, but keep verificationEmail in state
      state.registrationStep = 'form';
      state.error = null;
      // Note: verificationEmail stays in state and localStorage so user can return
    },
    clearPendingVerification: (state) => {
      // Explicitly clear pending verification (e.g., if user wants to start fresh)
      state.registrationStep = 'form';
      state.verificationEmail = null;
      state.error = null;
      localStorage.removeItem('pendingVerificationEmail');
    },
    setUser: (state, action) => {
      state.user = action.payload;
      // Only set authenticated if we have both token and user
      state.isAuthenticated = !!(state.token && action.payload);
    },
    initializeAuthStart: (state) => {
      state.initializing = true;
    },
    initializeAuthSuccess: (state, action) => {
      state.initializing = false;
      state.user = action.payload.user;
      // Ensure token is set from localStorage if not already in state
      if (!state.token) {
        state.token = localStorage.getItem('token');
      }
      state.isAuthenticated = !!(action.payload.user && state.token);
    },
    initializeAuthFailure: (state) => {
      state.initializing = false;
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      state.refreshToken = null;
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.registrationStep = 'form';
      state.verificationEmail = null;
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      // Note: We keep pendingVerificationEmail in localStorage even after logout
      // so user can still verify their email if they log back in
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  setRegistrationStep,
  verifyEmailStart,
  verifyEmailSuccess,
  verifyEmailFailure,
  setVerificationEmail,
  resetRegistration,
  clearPendingVerification,
  setUser,
  initializeAuthStart,
  initializeAuthSuccess,
  initializeAuthFailure,
  logout
} = authSlice.actions;
export default authSlice.reducer;
